import { Injectable } from '@angular/core';
import { Session } from '../models/session.model';

@Injectable({ providedIn: 'root' })
export class SessionService {

  private storageKey = 'fieldops_session';

  getSession(): Session | null {
    const data = localStorage.getItem(this.storageKey);
    if (!data) return null;

    try {
      return JSON.parse(data) as Session;
    } catch {
      // Corrupted storage — treat as no session
      this.clearSession();
      return null;
    }
  }

  setSession(session: Session): void {
    localStorage.setItem(this.storageKey, JSON.stringify(session));
  }

  clearSession(): void {
    localStorage.removeItem(this.storageKey);
  }

  isLoggedIn(): boolean {
    const session = this.getSession();
    if (!session) return false;

    // ✅ Validate the token isn't expired — presence alone is not enough
    if (this.isTokenExpired(session.accessToken)) {
      this.clearSession();
      return false;
    }

    return true;
  }

  getAccessToken(): string | null {
    const session = this.getSession();
    if (!session) return null;
    if (this.isTokenExpired(session.accessToken)) {
      this.clearSession();
      return null;
    }
    return session.accessToken;
  }

  getUserId(): string | null {
    const token = this.getAccessToken();
    if (!token) return null;
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']
          ?? decoded['sub']
          ?? decoded['nameid']
          ?? null;
    } catch {
      return null;
    }
  }

  // ── Token expiry check ──────────────────────────────────────
  private isTokenExpired(token: string | undefined | null): boolean {
    if (!token) return true;

    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));

      if (!decoded['exp']) return false; // no exp claim — can't check, assume valid

      const expiryMs = decoded['exp'] * 1000; // JWT exp is in seconds
      return Date.now() >= expiryMs;
    } catch {
      // Malformed token — treat as expired/invalid
      return true;
    }
  }
}